import React, {useState, useEffect, useMemo} from 'react';
import {Checkbox, List, ListItem, ListItemButton, Stack, Typography} from '@mui/material';
import {useChatContext} from '../context';
import {ContactOptionItem} from './ContactOptionItem';
import {SearchInput} from '@ui/SearchInput/SearchInput';
import {debounce} from 'lodash';
import { useTranslation } from 'react-i18next';

type Props = {
    selectedUsers: string[];
    onChange: (users: string[]) => void;
    canEdit: boolean;
    createMode?: boolean;
};

export const GroupChatUsersList: React.FC<Props> = ({
    selectedUsers,
    onChange,
    canEdit,
    createMode,
}) => {
    const {t} = useTranslation(['common', 'administration']);
    const {chatUsers} = useChatContext();
    const [search, setSearch] = useState('');
    const [filtered, setFiltered] = useState<string[]>([]);

    const debouncedSearch = useMemo(() => {
        return debounce((value: string) => {
            setFiltered(
                (chatUsers ?? []).filter((u) => u.toLowerCase().includes(value.toLowerCase())),
            );
        }, 200);
    }, [chatUsers]);

    useEffect(() => {
        debouncedSearch(search);
        return () => debouncedSearch.cancel();
    }, [search, debouncedSearch]);

    const sorted = useMemo(() => {
        const selectedSet = new Set(selectedUsers);
        const chosen = filtered.filter((u) => selectedSet.has(u));
        const others = filtered.filter((u) => !selectedSet.has(u));
        return [...chosen, ...others];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtered]);

    const toggle = (user: string) => {
        if (selectedUsers.includes(user)) {
            onChange(selectedUsers.filter((u) => u !== user));
        } else {
            onChange([...selectedUsers, user]);
        }
    };

    return (
        <Stack gap={1}>
            <SearchInput
                size='small'
                label={t('users', {ns: 'administration'})}
                value={search}
                onInput={setSearch}
                width='100%'
                variant='outlined'
            />
            <Typography variant='body1'>{t('users', {ns: 'administration'})}</Typography>

            <List
                sx={{
                    minHeight: 300,
                    maxHeight: 300,
                    overflowY: 'scroll',
                    borderRadius: 1,
                    border: '1px solid var(--mui-palette-divider)',
                }}
            >
                {sorted.length === 0 && (
                    <Typography
                        variant='body1'
                        sx={{textAlign: 'center', py: 2}}
                    >
                        {t('noUsersFound', {ns: 'administration'})}
                    </Typography>
                )}
                {sorted.map((user) => (
                    <ListItem
                        key={user}
                        disablePadding
                        alignItems='center'
                    >
                        <ListItemButton
                            disabled={!canEdit && !createMode}
                            onClick={() => toggle(user)}
                            dense
                            sx={{
                                justifyContent: 'space-between',
                            }}
                        >
                            <ContactOptionItem
                                option={user}
                                size={32}
                            />
                            <Checkbox
                                disabled={!canEdit && !createMode}
                                edge='start'
                                checked={selectedUsers.includes(user)}
                                tabIndex={-1}
                                size='small'
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Stack>
    );
};
